// Vidéo d'une animation (lot 7 : YouTube Shorts, TikTok, Reels) — PUR.
//
// La vidéo n'est pas filmée en temps réel : le worker la rend IMAGE PAR
// IMAGE. Chaque image vient de `videoFrameAt(plan, ms)` (scène, instant de la
// scène, sous-titre, question montrée) ; la piste audio est posée par FFmpeg
// aux instants de `plan.clips`. Image et son sortent du même calcul : calés
// à la milliseconde, quelle que soit la vitesse de la machine.
//
// Mêmes règles que le lecteur de l'app (`playback.ts`), sans élève :
//   - document joué : accroche + intro + scènes GRATUITES + générique de la
//     VIDÉO (`videoDocument`) — la partie payante (« À toi » : questions,
//     boîte à outils) n'est pas dans la vidéo (Philippe, 21/10), elle reste
//     dans l'app ; `interactive: true` la remettrait, en démonstration ;
//   - chaque partie a sa voix (ta voix, ou la voix de synthèse posée à
//     l'export) ; la phrase i se dit au début ÉTIRÉ de la phrase (la voix
//     attend l'image) : le morceau [V_i, V_i+1[ du fichier, posé là ;
//   - une question suit son `exportBehavior` : `auto-reveal` (la question,
//     puis la bonne réponse et l'explication), `cta` (« À toi sur
//     JeRaisonne ! »), `skip` (retirée). L'image s'arrête le temps de la
//     montrer ; si la voix est au milieu d'une phrase, la question attend
//     qu'elle l'ait finie, comme dans l'app ;
//   - une scène finie reste `SCENE_END_HOLD_MS` à l'écran (pas l'accroche,
//     ni la dernière scène) ; tout s'enchaîne sans clic ;
//   - sous-titres et panneau de question : remontés au-dessus de
//     l'interface des Shorts (titre en bas, boutons à droite) quand rien de
//     la figure n'est dessous ; sinon à leur place dans l'app, vérifiée par
//     le contrôle de mise en page. Pas de sous-titre pendant une question.

import { bookendOf, withBookends, type BookendKind } from './bookends';
import { stripInteractive } from './paywall';
import { voiceSlots, type VoiceSlot } from './voice';
import {
  CAPTION_GEOMETRY,
  captionTextHeight,
  PANEL_GEOMETRY,
  itemHitsRect,
  panelHeight,
  sceneLayout,
  type Rect,
} from './layout';
import { measureText } from './measure';
import { splitSentences } from './narration';
import { SCENE_END_HOLD_MS } from './playback';
import type { JrafDocument, JrafScene } from './schema/document';
import { isInteractiveStep, type JrafStep } from './schema/steps';
import {
  compileAnimation,
  type CompileOptions,
  type CompiledAnimation,
  type CompiledScene,
} from './timeline';

/** Images par seconde de la vidéo. */
export const VIDEO_FPS = 30;
/** Taille de la vidéo (9:16). */
export const VIDEO_WIDTH = 1080;
export const VIDEO_HEIGHT = 1920;
/** Question en `cta` ou outil : le temps de lire, puis la suite (ms). */
export const VIDEO_CTA_MIN_MS = 3500;
export const VIDEO_CTA_MAX_MS = 6000;
/** Bonne réponse et explication à l'écran (ms) : le temps de les lire. */
export const VIDEO_REVEAL_MIN_MS = 2500;
export const VIDEO_REVEAL_MAX_MS = 7000;
/** Pastille « étape HORA » d'une scène : visible, puis s'efface (comme dans l'app). */
export const VIDEO_CHIP_MS = 2200;
export const VIDEO_CHIP_FADE_MS = 400;
/** Durée maximale d'un YouTube Short (ms). */
export const SHORTS_MAX_MS = 180_000;
/** Bas de l'image couvert par l'interface des Shorts / TikTok (titre, légende), part de la hauteur. */
export const SHORTS_SAFE_BOTTOM = 0.2;
/** Colonne de droite couverte (boutons « j'aime », commentaires), part de la largeur. */
export const SHORTS_SAFE_RIGHT = 0.15;
/** Pas de la recherche de la position la plus haute du panneau (px du canevas). */
const LIFT_STEP = 8;

export type VideoSceneKind = BookendKind | 'content';

/** Morceau de la vidéo d'une scène : l'instant de la scène avance (ou reste figé). */
export interface VideoSpan {
  /** temps de la vidéo, depuis le début de la scène */
  fromMs: number;
  toMs: number;
  /** instant de la scène à `fromMs` */
  sceneMs: number;
  /** figé (question montrée, voix qui finit sa phrase) */
  frozen: boolean;
}

export interface VideoGatePlan {
  stepId: string;
  action: 'question.ask' | 'tool.open';
  mode: 'auto-reveal' | 'cta';
  /** depuis le début de la scène */
  openMs: number;
  /** bonne réponse montrée (`auto-reveal`) */
  revealMs: number | null;
  closeMs: number;
  /** bas du panneau, en px du canevas depuis le bas */
  panelBottom: number;
}

export interface VideoCaptionPlan {
  /** phrase de la narration */
  index: number;
  text: string;
  /** depuis le début de la scène */
  fromMs: number;
  toMs: number;
  /** marges du bandeau (px du canevas) : remonté au-dessus de l'interface des Shorts ou à sa place */
  bottom: number;
  left: number;
  right: number;
}

export interface VideoScenePlan {
  index: number;
  sceneId: string;
  kind: VideoSceneKind;
  /** début dans la vidéo */
  startMs: number;
  durationMs: number;
  spans: VideoSpan[];
  gates: VideoGatePlan[];
  captions: VideoCaptionPlan[];
}

/** Morceau d'une voix posé dans la vidéo. */
export interface VideoClip {
  assetId: string;
  /** chemin de la voix dans le bucket `animation-assets` */
  path: string;
  /** morceau du fichier (ms) */
  fromMs: number;
  toMs: number;
  /** où il commence dans la vidéo */
  atMs: number;
}

export interface VideoPlan {
  fps: number;
  width: number;
  height: number;
  durationMs: number;
  scenes: VideoScenePlan[];
  /** voix, dans l'ordre */
  clips: VideoClip[];
  /** jingle du début (intro) et de la fin (générique) ; la musique s'éteint au générique */
  introMs: number | null;
  outroMs: number | null;
  /** image de la vignette (l'accroche, sinon l'intro) */
  coverMs: number;
}

// ------------------------------------------------------------ document

export interface VideoOptions {
  /**
   * garder la partie « À toi » (questions, boîte à outils) en démonstration ;
   * par défaut, elle est retirée : c'est la partie payante, dans l'app
   */
  interactive?: boolean;
}

/**
 * Ce que la vidéo montre du document : sa partie gratuite (`stripInteractive` :
 * sans les scènes « À toi », leurs objets et leurs ressources), ou tout avec
 * `interactive: true`.
 */
export function videoSource(doc: JrafDocument, opts: VideoOptions = {}): JrafDocument {
  if (opts.interactive || !doc.scenes.some((s) => s.interactive)) return doc;
  try {
    return stripInteractive(doc).doc;
  } catch {
    return doc; // aucune scène gratuite (refusé par la validation) : tel quel
  }
}

/** Les voix de la vidéo (pas celles des scènes « À toi », absentes de la vidéo). */
export function videoVoiceSlots(doc: JrafDocument, opts: VideoOptions = {}): VoiceSlot[] {
  return voiceSlots(doc).filter((s) => opts.interactive || !s.interactive);
}

/**
 * Document de la vidéo : accroche + intro + scènes gratuites + générique de
 * la vidéo ; avec `interactive`, les étapes interactives en `skip` deviennent
 * une attente d'1 ms (même identifiant : les ancrages `with` qui les visent
 * restent valables).
 */
export function videoDocument(doc: JrafDocument, opts: VideoOptions = {}): JrafDocument {
  const played = withBookends(videoSource(doc, opts), { mode: 'video' });
  return {
    ...played,
    scenes: played.scenes.map((s) =>
      s.steps.some(skipped)
        ? { ...s, steps: s.steps.map((st) => (skipped(st) ? asWait(st) : st)) }
        : s,
    ),
  };
}

function skipped(st: JrafStep): boolean {
  return isInteractiveStep(st) && st.exportBehavior.mode === 'skip';
}

function asWait(st: JrafStep): JrafStep {
  return {
    id: st.id,
    action: 'timeline.wait',
    durationMs: 1,
    ...(st.with !== undefined ? { with: st.with } : {}),
    ...(st.atSentence !== undefined ? { atSentence: st.atSentence } : {}),
    ...(st.delayMs !== undefined ? { delayMs: st.delayMs } : {}),
  };
}

// ------------------------------------------------------------ calendrier

/** Temps de lecture d'un texte affiché (ms). */
export function readingMs(text: string): number {
  return 1200 + text.replace(/\s+/g, ' ').trim().length * 45;
}

const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));

/** Ce que la vidéo fait d'une question (porte) : durées et réponse montrée. */
export function gateBehavior(step: JrafStep): {
  mode: 'auto-reveal' | 'cta';
  revealAfterMs: number | null;
  holdMs: number;
} {
  if (step.action === 'question.ask') {
    const b = step.exportBehavior;
    if (b.mode === 'auto-reveal') {
      const read = clamp(readingMs(step.explanation), VIDEO_REVEAL_MIN_MS, VIDEO_REVEAL_MAX_MS);
      return { mode: 'auto-reveal', revealAfterMs: b.afterMs, holdMs: b.afterMs + read };
    }
    const text = [step.prompt, ...step.choices].join(' ');
    return {
      mode: 'cta',
      revealAfterMs: null,
      holdMs: clamp(readingMs(text), VIDEO_CTA_MIN_MS, VIDEO_CTA_MAX_MS),
    };
  }
  if (step.action === 'tool.open')
    return {
      mode: 'cta',
      revealAfterMs: null,
      holdMs: clamp(readingMs(step.prompt), VIDEO_CTA_MIN_MS, VIDEO_CTA_MAX_MS),
    };
  return { mode: 'cta', revealAfterMs: null, holdMs: 0 };
}

interface Freeze {
  /** instant de la scène où l'image s'arrête */
  at: number;
  /** la voix finit sa phrase */
  waitMs: number;
  /** question montrée */
  holdMs: number;
  step: JrafStep;
  behavior: ReturnType<typeof gateBehavior>;
}

function kindOf(scene: JrafScene): VideoSceneKind {
  return bookendOf(scene.id) ?? 'content';
}

function hasVoice(anim: CompiledAnimation, sc: CompiledScene): string | null {
  const id = sc.scene.narration.audioAssetId;
  if (!id) return null;
  const a = anim.doc.assets.find((x) => x.id === id);
  return a?.kind === 'audio' ? a.path : null;
}

function rectAbove(
  text: string,
  canvas: { width: number; height: number },
  m: { bottom: number; left: number; right: number },
): Rect {
  const g = CAPTION_GEOMETRY;
  const zone = canvas.width - m.left - m.right;
  const maxText = zone - 2 * g.padX;
  const t = measureText(text, g.size, 'normal', maxText);
  const width = Math.min(t.inkWidth, maxText) + 2 * g.padX;
  const height = captionTextHeight(t) + 2 * g.padY;
  const cx = m.left + zone / 2;
  const y1 = canvas.height - m.bottom;
  return { x0: cx - width / 2, x1: cx + width / 2, y0: y1 - height, y1 };
}

/** Objets de la figure que touche chaque rectangle, à ces instants de la scène. */
function hits(
  anim: CompiledAnimation,
  sc: CompiledScene,
  times: number[],
  rects: readonly Rect[],
): Set<string>[] {
  const out = rects.map(() => new Set<string>());
  for (const t of times)
    for (const it of sceneLayout(anim, sc, t))
      rects.forEach((r, k) => {
        if (itemHitsRect(it, r)) out[k]!.add(it.id);
      });
  return out;
}

const subset = (a: Set<string>, b: Set<string>) => [...a].every((x) => b.has(x));

function scenePlan(
  anim: CompiledAnimation,
  sc: CompiledScene,
  isLast: boolean,
): Omit<VideoScenePlan, 'startMs'> & { clips: VideoClip[] } {
  const kind = kindOf(sc.scene);
  const S = sc.sentenceStartsMs;
  const V = sc.voiceStartsMs;
  const voicePath = hasVoice(anim, sc);
  const len = (i: number) => Math.max(0, (V[i + 1] ?? sc.voiceTotalMs) - (V[i] ?? 0));

  // portes : la voix finit sa phrase, puis la question se montre
  const freezes: Freeze[] = sc.schedule.steps
    .filter((g) => g.gate)
    .sort((a, b) => a.startMs - b.startMs)
    .map((g) => {
      const step = sc.scene.steps[g.index]!;
      let waitMs = 0;
      if (voicePath)
        S.forEach((s, i) => {
          const end = s + len(i);
          if (s < g.startMs && end > g.startMs) waitMs = Math.max(waitMs, end - g.startMs);
        });
      const behavior = gateBehavior(step);
      return { at: g.startMs, waitMs, holdMs: behavior.holdMs, step, behavior };
    });

  /** instant de la vidéo (depuis le début de la scène) d'un instant de la scène */
  const local = (x: number) =>
    x + freezes.reduce((sum, f) => (f.at <= x ? sum + f.waitMs + f.holdMs : sum), 0);
  const before = (x: number) =>
    x + freezes.reduce((sum, f) => (f.at < x ? sum + f.waitMs + f.holdMs : sum), 0);

  const endScene = sc.schedule.endMs;
  const hold = isLast || kind === 'hook' ? 0 : SCENE_END_HOLD_MS;
  const durationMs = Math.round(local(endScene) + hold);

  // morceaux : l'image avance, s'arrête à chaque porte
  const spans: VideoSpan[] = [];
  let cursor = 0;
  let at = 0;
  for (const f of freezes) {
    if (f.at > cursor) {
      spans.push({ fromMs: at, toMs: at + (f.at - cursor), sceneMs: cursor, frozen: false });
      at += f.at - cursor;
      cursor = f.at;
    }
    const d = f.waitMs + f.holdMs;
    if (d > 0) {
      spans.push({ fromMs: at, toMs: at + d, sceneMs: f.at, frozen: true });
      at += d;
    }
  }
  spans.push({ fromMs: at, toMs: Math.max(at, durationMs), sceneMs: cursor, frozen: false });

  // questions montrées (panneau remonté au-dessus des Shorts si la figure le permet)
  const gates: VideoGatePlan[] = freezes.map((f) => {
    const openMs = before(f.at) + f.waitMs;
    const step = f.step as Extract<JrafStep, { action: 'question.ask' | 'tool.open' }>;
    return {
      stepId: step.id,
      action: step.action,
      mode: f.behavior.mode,
      openMs,
      revealMs: f.behavior.revealAfterMs !== null ? openMs + f.behavior.revealAfterMs : null,
      closeMs: openMs + f.holdMs,
      panelBottom: liftedPanel(anim, sc, step, f.at),
    };
  });

  // sous-titres (scènes du document seulement, comme dans l'app)
  const captions: VideoCaptionPlan[] = [];
  if (kind === 'content') {
    const texts = splitSentences(sc.scene.narration.text);
    texts.forEach((text, i) => {
      const from = S[i] ?? 0;
      const next = S[i + 1];
      const fromMs = Math.round(local(from));
      const toMs = next !== undefined ? Math.round(local(next)) : durationMs;
      if (toMs <= fromMs) return;
      captions.push({ index: i, text, fromMs, toMs, ...captionPlace(anim, sc, text, from, next) });
    });
  }

  // voix : la phrase i, à son début étiré
  const clips: VideoClip[] = [];
  if (voicePath)
    S.forEach((s, i) => {
      const fromMs = sc.voiceOffsetMs + (V[i] ?? 0);
      const toMs = fromMs + len(i);
      if (toMs - fromMs < 1) return;
      clips.push({
        assetId: sc.scene.narration.audioAssetId!,
        path: voicePath,
        fromMs: Math.round(fromMs),
        toMs: Math.round(toMs),
        atMs: Math.round(local(s)),
      });
    });

  return {
    index: sc.index,
    sceneId: sc.scene.id,
    kind,
    durationMs,
    spans,
    gates,
    captions,
    clips,
  };
}

/** Place d'un sous-titre : au-dessus de l'interface des Shorts si rien de la figure n'y est. */
function captionPlace(
  anim: CompiledAnimation,
  sc: CompiledScene,
  text: string,
  from: number,
  next: number | undefined,
): { bottom: number; left: number; right: number } {
  const canvas = anim.doc.canvas;
  const g = CAPTION_GEOMETRY;
  const app = { bottom: g.bottom, left: g.side, right: g.side };
  const lifted = {
    bottom: Math.round(canvas.height * SHORTS_SAFE_BOTTOM),
    left: g.side,
    right: Math.max(g.side, Math.round(canvas.width * SHORTS_SAFE_RIGHT)),
  };
  const end = next ?? sc.schedule.endMs;
  const times = [from, ...sc.schedule.steps.map((s) => s.startMs), Math.max(from, end - 1)].filter(
    (t) => t >= from && t < Math.max(end, from + 1),
  );
  const [base, up] = hits(anim, sc, times, [
    rectAbove(text, canvas, app),
    rectAbove(text, canvas, lifted),
  ]);
  return subset(up!, base!) ? lifted : app;
}

/** Bas du panneau d'une question : le plus haut possible jusqu'à la zone sûre des Shorts. */
function liftedPanel(
  anim: CompiledAnimation,
  sc: CompiledScene,
  step: JrafStep,
  at: number,
): number {
  const canvas = anim.doc.canvas;
  const g = PANEL_GEOMETRY;
  const h = panelHeight(step, canvas);
  const rect = (bottom: number): Rect => ({
    x0: g.side,
    x1: canvas.width - g.side,
    y0: canvas.height - bottom - h,
    y1: canvas.height - bottom,
  });
  const items = sceneLayout(anim, sc, at);
  const touched = (r: Rect) =>
    new Set(items.filter((it) => itemHitsRect(it, r)).map((it) => it.id));
  const base = touched(rect(g.bottom));
  const target = Math.round(canvas.height * SHORTS_SAFE_BOTTOM);
  for (let b = target; b > g.bottom; b -= LIFT_STEP) {
    if (canvas.height - b - h < 0) continue;
    if (subset(touched(rect(b)), base)) return b;
  }
  return g.bottom;
}

/** Morceaux de voix contigus (même fichier, sans attente) réunis en un seul. */
export function mergeClips(clips: readonly VideoClip[]): VideoClip[] {
  const out: VideoClip[] = [];
  for (const c of [...clips].sort((a, b) => a.atMs - b.atMs)) {
    const prev = out[out.length - 1];
    if (
      prev &&
      prev.assetId === c.assetId &&
      Math.abs(c.fromMs - prev.toMs) <= 1 &&
      Math.abs(c.atMs - (prev.atMs + prev.toMs - prev.fromMs)) <= 1
    ) {
      prev.toMs = c.toMs;
      continue;
    }
    out.push({ ...c });
  }
  return out;
}

/** Plan de la vidéo d'une animation compilée depuis `videoDocument`. */
export function videoPlan(anim: CompiledAnimation): VideoPlan {
  const scenes: VideoScenePlan[] = [];
  const clips: VideoClip[] = [];
  let t = 0;
  anim.scenes.forEach((sc, i) => {
    const p = scenePlan(anim, sc, i === anim.scenes.length - 1);
    const { clips: own, ...rest } = p;
    scenes.push({ ...rest, startMs: t });
    for (const c of own) clips.push({ ...c, atMs: c.atMs + t });
    t += p.durationMs;
  });
  const intro = scenes.find((s) => s.kind === 'intro');
  const outro = scenes.find((s) => s.kind === 'outro');
  const hook = scenes.find((s) => s.kind === 'hook');
  const coverScene = hook ?? intro ?? scenes[0];
  return {
    fps: VIDEO_FPS,
    width: VIDEO_WIDTH,
    height: VIDEO_HEIGHT,
    durationMs: t,
    scenes,
    clips: mergeClips(clips),
    introMs: intro?.startMs ?? null,
    outroMs: outro?.startMs ?? null,
    coverMs: coverScene ? Math.round(coverScene.startMs + coverScene.durationMs * 0.75) : 0,
  };
}

/** Document de la vidéo, compilé, et son plan. */
export function planVideo(
  doc: JrafDocument,
  opts: CompileOptions & VideoOptions = {},
): { anim: CompiledAnimation; plan: VideoPlan } {
  const { interactive, ...compile } = opts;
  const anim = compileAnimation(videoDocument(doc, { interactive }), compile);
  return { anim, plan: videoPlan(anim) };
}

// ------------------------------------------------------------- une image

export interface VideoFrame {
  /** scène jouée (index dans le document de la vidéo) */
  scene: number;
  kind: VideoSceneKind;
  /** instant de la scène (pour `sceneStateAt`, l'intro, le générique) */
  sceneMs: number;
  /** depuis le début de la scène, dans la vidéo */
  localMs: number;
  caption: VideoCaptionPlan | null;
  gate: { plan: VideoGatePlan; revealed: boolean; progress: number } | null;
  /** pastille « étape HORA » (scènes du document) */
  chipOpacity: number;
}

/** L'image de la vidéo à l'instant `ms`. */
export function videoFrameAt(plan: VideoPlan, ms: number): VideoFrame {
  const t = clamp(ms, 0, Math.max(0, plan.durationMs - 1));
  let sp = plan.scenes[0]!;
  for (const s of plan.scenes) if (s.startMs <= t) sp = s;
  const u = t - sp.startMs;
  let span = sp.spans[0]!;
  for (const s of sp.spans) if (s.fromMs <= u) span = s;
  const sceneMs = span.frozen ? span.sceneMs : span.sceneMs + (u - span.fromMs);
  const g = sp.gates.find((x) => x.openMs <= u && u < x.closeMs) ?? null;
  const gate = g
    ? {
        plan: g,
        revealed: g.revealMs !== null && u >= g.revealMs,
        progress: clamp((u - g.openMs) / Math.max(1, (g.revealMs ?? g.closeMs) - g.openMs), 0, 1),
      }
    : null;
  let caption: VideoCaptionPlan | null = null;
  if (!gate) for (const c of sp.captions) if (c.fromMs <= u && u < c.toMs) caption = c;
  const chipOpacity =
    sp.kind !== 'content'
      ? 0
      : u < VIDEO_CHIP_MS
        ? 1
        : clamp(1 - (u - VIDEO_CHIP_MS) / VIDEO_CHIP_FADE_MS, 0, 1);
  return { scene: sp.index, kind: sp.kind, sceneMs, localMs: u, caption, gate, chipOpacity };
}

/** Nombre d'images de la vidéo. */
export function frameCount(plan: Pick<VideoPlan, 'durationMs' | 'fps'>): number {
  return Math.max(1, Math.ceil((plan.durationMs * plan.fps) / 1000));
}
